import React from 'react';
import PropTypes from 'prop-types';
import Linkify from 'react-linkify';
import moment from 'moment';
import InboxMessageProp from '../../../propTypes/inboxMessageProp';
import ModalHeader from '../../../../../commonComponents/modal/ModalHeader';
import Modal from '../../../../../commonComponents/modal/Modal';
import translate from '../../../../../commonComponents/translate';
import { INBOX_MSG_TYPE } from '../../../../../services/utilityFunctions';

// since the user may sometimes pass a youtube url (which we can't display on an iframe),
// we need to extract the video id
// (see https://stackoverflow.com/questions/3452546/how-do-i-get-the-youtube-video-id-from-a-url)
// and then use it to build the embed url, if there is no video id, we just return the url
const getEmbedUrl = url => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  const videoId = match && match[2].length === 11 ? match[2] : null;
  return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
};

function InboxMessageDetailsModal({ t, message, onClose }) {
  const {
    type,
    title,
    image,
    info,
    url,
    feed,
    createdAt,
    survey,
    surveyContext,
    profileBlock,
  } = message;

  const getTitle = () => {
    switch (type) {
      case INBOX_MSG_TYPE.SURVEY:
      case INBOX_MSG_TYPE.HIGHLIGHT_SURVEY_QUESTION:
        return title || survey?.title || '';
      case INBOX_MSG_TYPE.NEWS:
        return feed?.title || title || '';
      case INBOX_MSG_TYPE.PROFILING:
        return t('inbox_profiling_title');
      default:
        return title;
    }
  };

  const canAnswer =
    surveyContext &&
    surveyContext.endDate >= Date.now() &&
    !surveyContext.isSubmitted;

  const getDescription = () => {
    switch (type) {
      case INBOX_MSG_TYPE.SURVEY:
      case INBOX_MSG_TYPE.HIGHLIGHT_SURVEY_QUESTION: {
        let description =
          surveyContext?.description || survey?.description || info || '';

        if (!surveyContext || surveyContext.endDate < Date.now()) {
          description += '\n';
          description += t('inbox_event_closed');
        } else if (surveyContext.isSubmitted) {
          description += '\n';
          description += t('inbox_survey_answered');
        }
        return description;
      }
      case INBOX_MSG_TYPE.NEWS:
        return feed?.content || info || '';
      case INBOX_MSG_TYPE.PROFILING:
        return t(
          'inbox_profiling_description',
          t(`inbox_profiling_description_${profileBlock}`),
        );
      default:
        return info;
    }
  };

  const getAction = () => {
    switch (type) {
      case INBOX_MSG_TYPE.SURVEY:
      case INBOX_MSG_TYPE.HIGHLIGHT_SURVEY_QUESTION:
        return canAnswer ? t('inbox_answerNow') : null;
      case INBOX_MSG_TYPE.EVENT:
        return canAnswer ? t('inbox_answerEvent') : null;
      case INBOX_MSG_TYPE.MAGAZINE:
        return t('inbox_readMagazine');
      case INBOX_MSG_TYPE.VIDEO:
        return t('inbox_watchVideo');
      case INBOX_MSG_TYPE.PODCAST:
        return t('inbox_listenPodcast');
      case INBOX_MSG_TYPE.PDF:
        return t('inbox_readPDF');
      default:
        return t('inbox_readMore');
    }
  };

  const getMediaContent = () => {
    switch (type) {
      case INBOX_MSG_TYPE.PODCAST:
        return url;
      case INBOX_MSG_TYPE.VIDEO: {
        return url ? getEmbedUrl(url) : null;
      }
      default:
        return null;
    }
  };

  return (
    <Modal id="inboxMessageDetailsModal" onClose={onClose}>
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content">
          <ModalHeader
            title={t(`inbox_type_${message.type}`)}
            onClose={() => onClose(null)}
          />
          <div className="modal-body">
            <div className="container-fluid news-item-modal">
              {image && (
                <div className="news-item-modal-image-container">
                  <img
                    src={image.thumb || image.url}
                    alt={title}
                    className="img-fluid"
                  />
                </div>
              )}
              <h3 className="news-item-modal-title">{getTitle()}</h3>
              <p className="news-item-modal-date">
                {moment(createdAt).utc().format('DD MMM, YYYY')}
              </p>
              <div className="news-item-modal-description">
                <Linkify>{getDescription()}</Linkify>
                {getMediaContent() && (
                  <iframe src={getMediaContent()} title={title} />
                )}
              </div>
              {url && getAction() && (
                <div className="news-item-modal-button-container">
                  <a
                    className="btn m-btn btn-dynamic news-item-modal-button"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getAction()}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

InboxMessageDetailsModal.propTypes = {
  t: PropTypes.func.isRequired,
  message: PropTypes.shape(InboxMessageProp.propType).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default translate(InboxMessageDetailsModal);
